
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ServerTimeHook {
  serverTime: Date;
  isLoading: boolean;
  error: string | null;
  syncTime: () => Promise<void>;
  calculateTimeWorked: (clockInTime: string) => { hours: number; minutes: number; seconds: number; totalHours: number };
  formatDuration: (hours: number, minutes: number, seconds: number) => string;
  formatServerTimeWithTimezone: () => string;
  isTimeSynced: boolean;
}

export const useServerTime = (): ServerTimeHook => {
  const [serverTime, setServerTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeOffset, setTimeOffset] = useState(0);
  const [isTimeSynced, setIsTimeSynced] = useState(false);

  const syncTime = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Syncing server time...');
      const { data, error } = await supabase.rpc('get_server_time');
      
      if (error) {
        console.error('Server time sync error:', error);
        throw error;
      }
      
      if (data) {
        // Server time is already in Central Time zone, use it directly
        const serverTimestamp = new Date(data);
        
        console.log('Server time sync successful:', {
          serverTime: serverTimestamp.toISOString(),
          localDisplay: serverTimestamp.toLocaleString('en-US', { timeZone: 'America/Chicago' })
        });
        
        setTimeOffset(0); // No offset needed since server returns Central Time
        setServerTime(serverTimestamp);
        setIsTimeSynced(true);
      }
    } catch (err) {
      console.error('Error syncing server time:', err);
      setError('Failed to sync with server time');
      setIsTimeSynced(false);
      // Use client time as fallback
      setTimeOffset(0);
      setServerTime(new Date());
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update server time every second based on offset
  useEffect(() => {
    const interval = setInterval(() => {
      const adjustedTime = new Date(Date.now() + timeOffset);
      setServerTime(adjustedTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [timeOffset]);

  // Initial sync
  useEffect(() => {
    syncTime();
  }, [syncTime]);

  const calculateTimeWorked = useCallback((clockInTime: string): { hours: number; minutes: number; seconds: number; totalHours: number } => {
    try {
      const clockIn = new Date(clockInTime);
      
      // Validate clock-in time
      if (isNaN(clockIn.getTime())) {
        console.error('Invalid clock-in time:', clockInTime);
        return { hours: 0, minutes: 0, seconds: 0, totalHours: 0 };
      }
      
      // Use server time directly (it already includes the timezone offset)
      const now = serverTime;
      
      // Calculate difference in milliseconds
      const diffMs = now.getTime() - clockIn.getTime();
      
      console.log('Time calculation:', {
        clockInTime: clockIn.toISOString(),
        currentServerTime: now.toISOString(),
        diffMs: diffMs,
        timeOffset: timeOffset,
        isTimeSynced: isTimeSynced
      });
      
      // Always ensure positive time difference
      const safeDiffMs = Math.max(0, diffMs);
      
      const totalSeconds = Math.floor(safeDiffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const totalHours = safeDiffMs / (1000 * 60 * 60);
      
      return { hours, minutes, seconds, totalHours };
    } catch (error) {
      console.error('Error calculating time worked:', error);
      return { hours: 0, minutes: 0, seconds: 0, totalHours: 0 };
    }
  }, [serverTime, timeOffset, isTimeSynced]);

  const formatDuration = useCallback((hours: number, minutes: number, seconds: number): string => {
    // Ensure all values are positive integers
    const h = Math.max(0, Math.floor(hours));
    const m = Math.max(0, Math.floor(minutes));
    const s = Math.max(0, Math.floor(seconds));
    
    if (h > 0) {
      return `${h}h ${m}m ${s}s`;
    } else if (m > 0) {
      return `${m}m ${s}s`;
    }
    return `${s}s`;
  }, []);

  const formatServerTimeWithTimezone = useCallback((): string => {
    // Format the server time as received (already in Central Time)
    const timeString = serverTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    
    // Get timezone abbreviation (CST/CDT)
    const tzString = serverTime.toLocaleTimeString('en-US', {
      timeZoneName: 'short',
      timeZone: 'America/Chicago'
    }).split(' ').pop() || 'CT';
    
    return `${timeString} ${tzString}`;
  }, [serverTime]);

  return {
    serverTime,
    isLoading,
    error,
    syncTime,
    calculateTimeWorked,
    formatDuration,
    formatServerTimeWithTimezone,
    isTimeSynced
  };
};
